/**
 * Phototron WebGL PBR Renderer
 * Renders materials with dynamic lighting using PBR Specular Workflow
 */

import { VERTEX_SHADER, FRAGMENT_SHADER_FULL, FRAGMENT_SHADER_SIMPLE } from './shaders';
import { TextureUrls, LightingConfig, DEFAULT_LIGHTING } from './types';

export interface RendererOptions {
  canvas: HTMLCanvasElement;
  textures: TextureUrls;
  lighting?: Partial<LightingConfig>;
  materialScale?: number;
  verticalOffset?: number;
  useSimpleShader?: boolean;
  progressiveLoading?: boolean;
}

interface UniformLocations {
  albedo: WebGLUniformLocation | null;
  normal: WebGLUniformLocation | null;
  specular: WebGLUniformLocation | null;
  roughnessMap: WebGLUniformLocation | null;
  metalnessMap: WebGLUniformLocation | null;
  hasRoughnessMap: WebGLUniformLocation | null;
  hasMetalnessMap: WebGLUniformLocation | null;
  hasSpecularMap: WebGLUniformLocation | null;
  relight: WebGLUniformLocation | null;
  lightPos: WebGLUniformLocation | null;
  time: WebGLUniformLocation | null;
  textureSize: WebGLUniformLocation | null;
  canvasSize: WebGLUniformLocation | null;
  ambientIntensity: WebGLUniformLocation | null;
  diffuseIntensity: WebGLUniformLocation | null;
  specularMultiplier: WebGLUniformLocation | null;
  fresnelIntensity: WebGLUniformLocation | null;
  accentColor: WebGLUniformLocation | null;
  accentIntensity: WebGLUniformLocation | null;
  materialScale: WebGLUniformLocation | null;
  verticalOffset: WebGLUniformLocation | null;
}

export class WebGLRenderer {
  private canvas: HTMLCanvasElement;
  private gl: WebGLRenderingContext;
  private program: WebGLProgram;
  private uniforms: UniformLocations;
  private textures: {
    albedo: WebGLTexture; normal: WebGLTexture; specular: WebGLTexture;
    roughness: WebGLTexture; metalness: WebGLTexture;
  };
  // Which optional maps were actually provided — drives the shader workflow.
  private hasSpecular = false;
  private hasRoughness = false;
  private hasMetalness = false;
  private textureSize: { width: number; height: number } = { width: 1, height: 1 };
  private lighting: LightingConfig;
  private materialScale: number;
  private verticalOffset: number;
  private progressiveLoading: boolean;

  private lightX = 0;
  private lightY = 0.3;
  private targetLightX = 0;
  private targetLightY = 0.3;
  // 0 = idle (full neutral environment), 1 = pointer relighting (env dims to a
  // subtle fill, the key light takes over). Eased in the render loop.
  private relight = 0;
  private targetRelight = 0;
  private animationFrame: number | null = null;
  private isDestroyed = false;

  constructor(options: RendererOptions) {
    this.canvas = options.canvas;
    this.lighting = { ...DEFAULT_LIGHTING, ...options.lighting };
    this.materialScale = options.materialScale ?? 1.0;
    this.verticalOffset = options.verticalOffset ?? 0.0;
    this.progressiveLoading = options.progressiveLoading ?? true;

    // Get WebGL context
    const gl = this.canvas.getContext('webgl') || this.canvas.getContext('experimental-webgl');
    if (!gl) {
      throw new Error('WebGL not supported');
    }
    this.gl = gl as WebGLRenderingContext;

    // Create shader program
    const fragmentShader = options.useSimpleShader ? FRAGMENT_SHADER_SIMPLE : FRAGMENT_SHADER_FULL;
    this.program = this.createProgram(VERTEX_SHADER, fragmentShader);

    // Get uniform locations
    this.uniforms = this.getUniformLocations();

    // Setup geometry
    this.setupGeometry();

    // Load textures
    this.textures = {
      albedo: this.gl.createTexture()!,
      normal: this.gl.createTexture()!,
      specular: this.gl.createTexture()!,
      roughness: this.gl.createTexture()!,
      metalness: this.gl.createTexture()!,
    };

    this.loadTextures(options.textures);
  }

  private createShader(type: number, source: string): WebGLShader {
    const gl = this.gl;
    const shader = gl.createShader(type);
    if (!shader) {
      throw new Error('Failed to create shader');
    }

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const info = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(`Shader compile error: ${info}`);
    }

    return shader;
  }

  private createProgram(vertexSource: string, fragmentSource: string): WebGLProgram {
    const gl = this.gl;
    const vertexShader = this.createShader(gl.VERTEX_SHADER, vertexSource);
    const fragmentShader = this.createShader(gl.FRAGMENT_SHADER, fragmentSource);

    const program = gl.createProgram();
    if (!program) {
      throw new Error('Failed to create program');
    }

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const info = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      throw new Error(`Program link error: ${info}`);
    }

    return program;
  }

  private getUniformLocations(): UniformLocations {
    const gl = this.gl;
    return {
      albedo: gl.getUniformLocation(this.program, 'u_albedo'),
      normal: gl.getUniformLocation(this.program, 'u_normal'),
      specular: gl.getUniformLocation(this.program, 'u_specular'),
      roughnessMap: gl.getUniformLocation(this.program, 'u_roughnessMap'),
      metalnessMap: gl.getUniformLocation(this.program, 'u_metalnessMap'),
      hasRoughnessMap: gl.getUniformLocation(this.program, 'u_hasRoughnessMap'),
      hasMetalnessMap: gl.getUniformLocation(this.program, 'u_hasMetalnessMap'),
      hasSpecularMap: gl.getUniformLocation(this.program, 'u_hasSpecularMap'),
      relight: gl.getUniformLocation(this.program, 'u_relight'),
      lightPos: gl.getUniformLocation(this.program, 'u_lightPos'),
      time: gl.getUniformLocation(this.program, 'u_time'),
      textureSize: gl.getUniformLocation(this.program, 'u_textureSize'),
      canvasSize: gl.getUniformLocation(this.program, 'u_canvasSize'),
      ambientIntensity: gl.getUniformLocation(this.program, 'u_ambientIntensity'),
      diffuseIntensity: gl.getUniformLocation(this.program, 'u_diffuseIntensity'),
      specularMultiplier: gl.getUniformLocation(this.program, 'u_specularMultiplier'),
      fresnelIntensity: gl.getUniformLocation(this.program, 'u_fresnelIntensity'),
      accentColor: gl.getUniformLocation(this.program, 'u_accentColor'),
      accentIntensity: gl.getUniformLocation(this.program, 'u_accentIntensity'),
      materialScale: gl.getUniformLocation(this.program, 'u_materialScale'),
      verticalOffset: gl.getUniformLocation(this.program, 'u_verticalOffset'),
    };
  }

  private setupGeometry(): void {
    const gl = this.gl;

    // Fullscreen quad positions
    const positions = new Float32Array([
      -1, -1, 1, -1, -1, 1,
      -1, 1, 1, -1, 1, 1,
    ]);

    // Texture coordinates
    const texCoords = new Float32Array([
      0, 1, 1, 1, 0, 0,
      0, 0, 1, 1, 1, 0,
    ]);

    // Position buffer
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(this.program, 'a_position');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    // TexCoord buffer
    const texCoordBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW);

    const texCoordLocation = gl.getAttribLocation(this.program, 'a_texCoord');
    gl.enableVertexAttribArray(texCoordLocation);
    gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0);
  }

  // The maps to load, in texture-unit order. specular/roughness/metalness are
  // optional; only the ones the export actually provides are loaded + bound.
  private mapList(urls: TextureUrls): Array<{
    full: string; preview?: string; texture: WebGLTexture; unit: number; storeSize: boolean;
  }> {
    const maps = [
      { full: urls.albedo, preview: urls.albedoPreview, texture: this.textures.albedo, unit: 0, storeSize: true },
      { full: urls.normal, preview: urls.normalPreview, texture: this.textures.normal, unit: 1, storeSize: false },
    ];
    if (urls.specular) maps.push({ full: urls.specular, preview: urls.specularPreview, texture: this.textures.specular, unit: 2, storeSize: false });
    if (urls.roughness) maps.push({ full: urls.roughness, preview: urls.roughnessPreview, texture: this.textures.roughness, unit: 3, storeSize: false });
    if (urls.metalness) maps.push({ full: urls.metalness, preview: urls.metalnessPreview, texture: this.textures.metalness, unit: 4, storeSize: false });
    return maps;
  }

  private loadTextures(urls: TextureUrls): void {
    this.hasSpecular = !!urls.specular;
    this.hasRoughness = !!urls.roughness;
    this.hasMetalness = !!urls.metalness;

    const maps = this.mapList(urls);
    let loadedCount = 0;
    const total = maps.length;

    const onPreviewLoaded = () => {
      loadedCount++;
      if (loadedCount === total) {
        this.start();
        // Load full resolution textures if progressive loading is enabled
        if (this.progressiveLoading && maps.some((m) => m.preview)) {
          this.loadFullTextures(urls);
        }
      }
    };

    for (const m of maps) {
      const url = this.progressiveLoading && m.preview ? m.preview : m.full;
      this.loadTexture(url, m.texture, m.unit, m.storeSize, onPreviewLoaded);
    }
  }

  private loadFullTextures(urls: TextureUrls): void {
    for (const m of this.mapList(urls)) {
      if (m.preview) this.loadTexture(m.full, m.texture, m.unit, m.storeSize);
    }
  }

  private loadTexture(
    url: string,
    texture: WebGLTexture,
    unit: number,
    storeSize: boolean,
    onLoad?: () => void
  ): void {
    const gl = this.gl;

    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture);

    // Placeholder
    gl.texImage2D(
      gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([128, 128, 128, 255])
    );

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (this.isDestroyed) return;

      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);

      if (storeSize) {
        this.textureSize = { width: image.width, height: image.height };
      }

      // Texture parameters
      if (this.isPowerOf2(image.width) && this.isPowerOf2(image.height)) {
        gl.generateMipmap(gl.TEXTURE_2D);
      } else {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      }
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

      onLoad?.();
    };
    image.onerror = () => {
      console.error(`Failed to load texture: ${url}`);
      onLoad?.();
    };
    image.src = url;
  }

  private isPowerOf2(value: number): boolean {
    return (value & (value - 1)) === 0;
  }

  private resizeCanvas(): void {
    const displayWidth = this.canvas.clientWidth;
    const displayHeight = this.canvas.clientHeight;
    const dpr = window.devicePixelRatio || 1;

    const width = Math.floor(displayWidth * dpr);
    const height = Math.floor(displayHeight * dpr);

    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
      this.gl.viewport(0, 0, width, height);
    }
  }

  private render = (time: number): void => {
    if (this.isDestroyed) return;

    this.resizeCanvas();

    const gl = this.gl;

    // Smooth light movement
    this.lightX += (this.targetLightX - this.lightX) * 0.1;
    this.lightY += (this.targetLightY - this.lightY) * 0.1;
    // Soft handover between the neutral environment and the pointer light
    // (slightly slower than the light itself, so the dim reads as deliberate).
    this.relight += (this.targetRelight - this.relight) * 0.06;

    // Clear
    gl.clearColor(0.02, 0.02, 0.02, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Enable blending
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    // Use program
    gl.useProgram(this.program);

    // Bind textures
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.albedo);
    gl.uniform1i(this.uniforms.albedo, 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.normal);
    gl.uniform1i(this.uniforms.normal, 1);

    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.specular);
    gl.uniform1i(this.uniforms.specular, 2);

    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.roughness);
    gl.uniform1i(this.uniforms.roughnessMap, 3);

    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.metalness);
    gl.uniform1i(this.uniforms.metalnessMap, 4);

    // Workflow flags — tell the shader which maps are real (vs the 1×1 grey
    // placeholder) so it picks the metallic-roughness path or the fallback.
    if (this.uniforms.hasRoughnessMap) gl.uniform1f(this.uniforms.hasRoughnessMap, this.hasRoughness ? 1.0 : 0.0);
    if (this.uniforms.hasMetalnessMap) gl.uniform1f(this.uniforms.hasMetalnessMap, this.hasMetalness ? 1.0 : 0.0);
    if (this.uniforms.hasSpecularMap) gl.uniform1f(this.uniforms.hasSpecularMap, this.hasSpecular ? 1.0 : 0.0);
    if (this.uniforms.relight) gl.uniform1f(this.uniforms.relight, this.relight);

    // Set light position
    const lightZ = 0.7;
    const len = Math.sqrt(this.lightX ** 2 + this.lightY ** 2 + lightZ ** 2);
    gl.uniform3f(this.uniforms.lightPos, this.lightX / len, this.lightY / len, lightZ / len);

    // Set other uniforms
    if (this.uniforms.time) gl.uniform1f(this.uniforms.time, time * 0.001);
    gl.uniform2f(this.uniforms.textureSize, this.textureSize.width, this.textureSize.height);
    gl.uniform2f(this.uniforms.canvasSize, this.canvas.width, this.canvas.height);

    // Lighting uniforms
    if (this.uniforms.ambientIntensity) gl.uniform1f(this.uniforms.ambientIntensity, this.lighting.ambientIntensity);
    if (this.uniforms.diffuseIntensity) gl.uniform1f(this.uniforms.diffuseIntensity, this.lighting.diffuseIntensity);
    if (this.uniforms.specularMultiplier) gl.uniform1f(this.uniforms.specularMultiplier, this.lighting.specularMultiplier);
    if (this.uniforms.fresnelIntensity) gl.uniform1f(this.uniforms.fresnelIntensity, this.lighting.fresnelIntensity);
    if (this.uniforms.accentColor) gl.uniform3fv(this.uniforms.accentColor, this.lighting.accentColor);
    if (this.uniforms.accentIntensity) gl.uniform1f(this.uniforms.accentIntensity, this.lighting.accentIntensity);
    if (this.uniforms.materialScale) gl.uniform1f(this.uniforms.materialScale, this.materialScale);
    if (this.uniforms.verticalOffset) gl.uniform1f(this.uniforms.verticalOffset, this.verticalOffset);

    // Draw
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    this.animationFrame = requestAnimationFrame(this.render);
  };

  public start(): void {
    if (this.animationFrame === null) {
      this.animationFrame = requestAnimationFrame(this.render);
    }
  }

  public stop(): void {
    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  public setLightPosition(x: number, y: number): void {
    this.targetLightX = Math.max(-1, Math.min(1, x));
    this.targetLightY = Math.max(-1, Math.min(1, y));
  }

  /** 1 = pointer is relighting (environment steps back), 0 = idle. */
  public setRelight(active: boolean): void {
    this.targetRelight = active ? 1 : 0;
  }

  public setLighting(config: Partial<LightingConfig>): void {
    this.lighting = { ...this.lighting, ...config };
  }

  public setMaterialScale(scale: number): void {
    this.materialScale = scale;
  }

  public setVerticalOffset(offset: number): void {
    this.verticalOffset = offset;
  }

  public destroy(): void {
    this.isDestroyed = true;
    this.stop();

    const gl = this.gl;
    gl.deleteProgram(this.program);
    gl.deleteTexture(this.textures.albedo);
    gl.deleteTexture(this.textures.normal);
    gl.deleteTexture(this.textures.specular);
    gl.deleteTexture(this.textures.roughness);
    gl.deleteTexture(this.textures.metalness);
  }
}
